import React, { useState, useRef } from "react";
import { Button, Dropdown, Menu, Input, message, InputRef, Select } from "antd";
import { TestSet } from "../../model_gen/evaluation";
import { TestSetService } from "../../services/TestSetService";
import { usePermissions } from "../../PermissionsProvider";

interface TestSetContextMenuProps {
  applicationId: string;
  applicationActionType: string;
  requestId: string;
  testSets: TestSet[];
  existingTestSetIds?: string[];
  onUpdate?: (requestId: string, testSetIds: string[]) => void;
  onOpen?: (isOpen: boolean) => void;
  children: React.ReactNode;
}

const TestSetContextMenu: React.FC<TestSetContextMenuProps> = ({
  applicationId,
  applicationActionType,
  requestId,
  testSets,
  existingTestSetIds = [],
  onUpdate,
  onOpen,
  children,
}) => {
  const { apiKey } = usePermissions();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newTestSetName, setNewTestSetName] = useState("");
  const inputRef = useRef<InputRef>(null);
  const [selectedTestSet, setSelectedTestSet] = useState<TestSet | null>(null);
  const [newTestSetData, setNewTestSetData] = useState<{ name: string; description?: string } | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [isTagging, setIsTagging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTestSetDescription, setNewTestSetDescription] = useState("");

  const handleTestSetSelect = (testSet: TestSet) => {
    setSelectedTestSet(testSet);
    setNewTestSetData(null);
    setIsTagging(true);
  };

  const handleNewTestSetSubmit = (e?: React.KeyboardEvent<HTMLInputElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!newTestSetName.trim()) return;
    
    setNewTestSetData({ 
      name: newTestSetName.trim(),
      description: newTestSetDescription.trim() 
    });
    setSelectedTestSet(null);
    setIsTagging(true);
    setIsAddingNew(false);
    setNewTestSetName("");
    setNewTestSetDescription("");
  };

  const handleSubmitWithTags = () => {
    setIsSubmitting(true);
    const payload = {
      request_id: requestId,
      application_id: applicationId,
      action_type: applicationActionType,
      test_set: selectedTestSet || {
        name: newTestSetData?.name,
        description: newTestSetData?.description,
        application_id: applicationId,
        action_type: applicationActionType,
      },
      tags: tags
    };


    TestSetService.addRequestToTestSet(payload, null, apiKey).subscribe(
      (response: any) => {
        message.success("Added to test set successfully");
        
        const newTestSetId = String(response.id);
        const updatedTestSetIds = [...existingTestSetIds, newTestSetId];
        const uniqueTestSetIds = Array.from(new Set(updatedTestSetIds));
        
        onUpdate?.(requestId, uniqueTestSetIds);
        setIsDropdownOpen(false);
        setIsTagging(false);
        setTags([]);
        setSelectedTestSet(null);
        setNewTestSetData(null);
        setIsSubmitting(false);
      },
      (error: any) => {
        console.error("Failed to add to test set:", error);
        message.error("Failed to add to test set");
        setIsSubmitting(false);
      }
    );
  };

  const menu = (
    <Menu>
      {isTagging ? (
        <div style={{ padding: "8px 16px" }}>
          <div style={{ marginBottom: 8 }}>
            <strong>Selected: </strong>
            {selectedTestSet?.name || newTestSetData?.name}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4 }}><strong>Tags:</strong></label>
            <Select
              mode="tags"
              style={{ width: "100%", maxWidth: "250px" }}
              placeholder="Add tags"
              value={tags}
              onChange={setTags}
              tokenSeparators={[","]}
              dropdownStyle={{ display: "none" }}
              onClick={(e) => e.stopPropagation()}
              maxTagTextLength={10}
              maxTagCount={5}
              disabled={isSubmitting}
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={() => {
                setIsTagging(false);
                setTags([]);
                setSelectedTestSet(null);
                setNewTestSetData(null);
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleSubmitWithTags}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Submit
            </Button>
          </div>
        </div>
      ) : isAddingNew ? (
        <div style={{ padding: "8px 16px", minWidth: "250px" }}>
          <div style={{ marginBottom: 16, fontWeight: "bold" }}>New Test Set</div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4 }}><strong>Name *</strong></label>
            <Input
              ref={inputRef}
              autoFocus
              placeholder="Test set name"
              value={newTestSetName}
              onChange={(e) => setNewTestSetName(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", marginBottom: 4 }}><strong>Description</strong></label>
            <Input.TextArea
              placeholder="Test set description"
              value={newTestSetDescription}
              onChange={(e) => setNewTestSetDescription(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoSize={{ minRows: 1, maxRows: 3 }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              size="small"
              onClick={() => {
                setIsAddingNew(false);
                setNewTestSetName("");
                setNewTestSetDescription("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="small"
              disabled={!newTestSetName.trim()}
              onClick={() => handleNewTestSetSubmit()}
            >
              Next
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ fontWeight: "bold", marginBottom: 8 }}>Add to Test Set</div>
            <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
              Choose an existing test set or create a new one
            </div>
          </div>
          {testSets.length > 0 ? (
            testSets.map((testSet) => (
              <Menu.Item 
                key={testSet.id}
                disabled={existingTestSetIds.includes(String(testSet.id))}
              >
                <div onClick={(e) => {
                  e.stopPropagation();
                  if (!existingTestSetIds.includes(String(testSet.id))) {
                    handleTestSetSelect(testSet);
                  }
                }}>
                  {testSet.name}
                  {existingTestSetIds.includes(String(testSet.id)) && (
                    <span style={{ marginLeft: 8, color: "#8c8c8c" }}>(added)</span>
                  )}
                </div>
              </Menu.Item>
            ))
          ) : (
            <Menu.Item disabled>
              <div style={{ color: "#8c8c8c" }}>No existing test sets</div>
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item key="new-test-set">
            <div onClick={(e) => {
              e.stopPropagation();
              setIsAddingNew(true);
            }}>
              <span>Create New Test Set</span>
            </div>
          </Menu.Item>
        </>
      )}
    </Menu>
  );

  return (
    <Dropdown 
      overlay={menu} 
      trigger={["click"]}
      open={isDropdownOpen}
      onOpenChange={(visible) => {
        if (!isTagging && !isAddingNew) {
          setIsDropdownOpen(visible);
          onOpen?.(visible);
          if (!visible) {
            setIsAddingNew(false);
            setNewTestSetName("");
          }
        }
      }}
    >
      {children}
    </Dropdown>
  );
};

export default TestSetContextMenu;